import React from "react";
import PropTypes from "prop-types";

/**
 * Renders the sheets held by useSheetStack: the rail wrapper, the screen-reader
 * live region, and one wrapper per sheet carrying the refusal pulse.
 *
 * Presentational only — every decision (what exists, in what order, what is
 * flagged, what is blocked) belongs to the hook. This just draws it, so all
 * six sections get the same a11y and the same pulse behaviour for free.
 */
const SheetStack = ({ stack, className, describe, children }) => {
  if (!stack.isOpen) return null;

  return (
    <div className={className}>
      {/* A refused click changes nothing on screen except a pulse, which a
          screen reader cannot see. */}
      <span className="zy-sr-live" role="status" aria-live="polite">
        {stack.notice}
      </span>

      {stack.sheets.map((sheet, i) => (
        <div
          key={sheet.id}
          ref={(node) => stack.setNode(sheet.id, node)}
          className="zy-sheet"
          // N near-identical sheets are on screen. Without this a screen
          // reader announces several unlabelled groups and the ordinal badge
          // just runs into the label text.
          role="group"
          aria-label={
            describe(sheet, i) +
            (stack.sheets.length > 1
              ? ` — sheet ${i + 1} of ${stack.sheets.length}`
              : "")
          }
        >
          {/* Keyed on the restart token so each refusal REMOUNTS the node and
              the animation genuinely replays. Re-adding a class cannot do
              this: React never touches an attribute whose string is
              unchanged, and a CSS animation only restarts on a new node or a
              new animation-name. */}
          {stack.nudge.id === sheet.id && (
            <span key={stack.nudge.n} className="zy-sheet-pulse" aria-hidden="true" />
          )}
          {children(sheet, stack.sheetProps(sheet, i))}
        </div>
      ))}
    </div>
  );
};

SheetStack.propTypes = {
  /** The object returned by useSheetStack. */
  stack: PropTypes.object.isRequired,
  /** The rail's own class — cc3-editor / li3-editor / fh3-editor. */
  className: PropTypes.string.isRequired,
  /** (sheet, index) => accessible name for this sheet, e.g. "Add allergy". */
  describe: PropTypes.func.isRequired,
  /** (sheet, sheetProps) => the section's form element. */
  children: PropTypes.func.isRequired,
};

export default SheetStack;
